import {MouseEventHandler, useContext} from "react";
import {useTranslation} from "react-i18next";
import SearchFilters from "../../common/components/SearchFilters";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {LocationDiscountGetData} from "./Types/locationDiscount";

const LocationDiscountFilters = ({
                                     handleClickResetSearch,
                                     handleGetLocationDiscountList,
                                     testFilters,
                                     setTestFilters,
                                     responseData
                                 }: {
    handleGetLocationDiscountList: MouseEventHandler<HTMLButtonElement>;
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>;
    testFilters: any
    setTestFilters: any
    responseData: LocationDiscountGetData
}) => {

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "location",
            value: testFilters.locationCodes as SelectElementModelFE,
            label: `Location`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, locationCodes: value}),
            options: (responseData as LocationDiscountGetData)?.locations.map(el => ({
                code: el.id,
                label: el.name
            })).sort((a, b) =>
                a.label > b.label ? 1 : -1,
            ) || [],
            type: "AutocompleteM",
        },
        {
            name: "profile",
            value: testFilters.lppProfileIds as SelectElementModelFE,
            label: `Profiles`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, lppProfileIds: value}),
            options: (responseData as LocationDiscountGetData)?.profiles?.map(el => ({
                code: el.id,
                label: el.name
            })).sort((a, b) =>
                a.label > b.label ? 1 : -1,
            ) || [],
            type: "AutocompleteM",
        },
        {
            name: "discounts",
            value: testFilters.discounts as number,
            label: `Discount`,
            handleChange: (value: number) =>
                setTestFilters({...testFilters, discounts: value}),
            options: (responseData as LocationDiscountGetData)?.discountRange.map(el => el) || [],
            type: "AutocompleteM",
        },
    ];
    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "from",
            value: testFilters.validityFrom as string,
            options: [],
            label: `${t("pricing:ancillary.form.labels.reservationFrom")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    validityFrom: value,
                }),
            type: "Date",
        },
        {
            name: "to",
            value: testFilters.validityTo as string,
            options: [],
            label: `${t("pricing:ancillary.form.labels.reservationTo")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    validityTo: value,
                }),
            type: "Date",
        },
        {
            name: "pickUpFrom",
            value: testFilters.pickUpDateFrom as string,
            options: [],
            label: `${t("pricing:ancillary.form.labels.pickupDateFrom")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    pickUpDateFrom: value,
                }),
            type: "Date",
        },
        {
            name: "pickUpTo",
            value: testFilters.pickUpDateTo as string,
            options: [],
            label: `${t("pricing:ancillary.form.labels.pickupDateTo")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    pickUpDateTo: value,
                }),
            type: "Date",
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    legalEntities: value,
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:upsellingRate.filter.labels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
    ];

    return (
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleGetLocationDiscountList}
            handleClickResetSearch={handleClickResetSearch}
        />
    );
};

export default LocationDiscountFilters;
